import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ComingSoon from "@/components/site/ComingSoon";
import CookieConsent from "@/components/site/CookieConsent";
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ServicesPage from "@/pages/ServicesPage";
import SolutionsPage from "@/pages/SolutionsPage";
import TeamPage from "@/pages/TeamPage";
import ContactPage from "@/pages/ContactPage";
import ProductsPage from "@/pages/ProductsPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import CheckoutPage from "@/pages/CheckoutPage";
import LegalPage from "@/pages/LegalPage";
import { cookiePolicy, disclaimer } from "@/data/legal";
import { privacyPolicy } from "@/data/legal-privacy";
import { termsAndConditions } from "@/data/legal-terms";
import { refundPolicy } from "@/data/legal-refund";
import { shippingPolicy } from "@/data/legal-shipping";
import { initLenis, scrollTop } from "@/lib/scroll";
import Seo from "@/components/site/Seo";

function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.4 });
  return (
    <motion.div
      style={{ scaleX }}
      className="fixed inset-x-0 top-0 z-[90] h-[2px] origin-left bg-gradient-to-r from-blue-600 to-violet-600"
      aria-hidden="true"
    />
  );
}

function RouteReset() {
  const { pathname } = useLocation();
  useEffect(() => {
    scrollTop(true);
  }, [pathname]);
  return null;
}

export default function App() {
  useEffect(() => {
    initLenis();
  }, []);

  return (
    <div className="App bg-white font-sans text-slate-900 antialiased">
      <BrowserRouter>
        <ScrollProgress />
        <RouteReset />
        <Header />
        <Routes>
          <Route path="/" element={<><Seo title="Bytevora — Building Technology for What's Next" description="Bytevora builds modern software, web and mobile applications, SaaS platforms, cloud solutions, AI, automation, and scalable digital products." path="/" /><HomePage /></>} />
          <Route path="/about" element={<><Seo title="About Bytevora" description="Learn about Bytevora IT Solutions Private Limited — a technology company building modern software, digital products, cloud platforms, and intelligent systems from Lucknow, India." path="/about" /><AboutPage /></>} />
          <Route path="/services" element={<><Seo title="Services — Software, Cloud, AI & Digital Transformation" description="Explore Bytevora services: software and product development, web and mobile, AI and automation, cloud and infrastructure, digital transformation, and technology consulting." path="/services" /><ServicesPage /></>} />
          <Route path="/solutions" element={<><Seo title="Technology Solutions | SaaS, AI, Automation & Cloud | Bytevora" description="Explore Bytevora solutions for startups, SaaS product development, business automation, AI integration, cloud modernization, and modern digital systems." path="/solutions" /><SolutionsPage /></>} />
          <Route path="/team" element={<><Seo title="Our Team" description="Meet the leadership behind Bytevora and learn how we are building a focused technology team around software engineering, cloud, AI, automation, and digital product development." path="/team" /><TeamPage /></>} />
          <Route path="/contact" element={<><Seo title="Contact Bytevora" description="Start a project or enquire about software, cloud, AI, and digital product development with Bytevora. Email hello@bytevora.com or call +91 93053 83099." path="/contact" /><ContactPage /></>} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/products/:slug" element={<ProductDetailPage />} />
          <Route path="/checkout/:slug" element={<CheckoutPage />} />
          <Route path="/privacy-policy" element={<><Seo title="Privacy Policy" description="How Bytevora collects, uses, stores, shares, and protects personal information when you visit our website or interact with our services." path="/privacy-policy" /><LegalPage content={privacyPolicy} /></>} />
          <Route path="/cookie-policy" element={<><Seo title="Cookie Policy" description="How Bytevora uses cookies and similar technologies, and how you can manage your preferences." path="/cookie-policy" /><LegalPage content={cookiePolicy} /></>} />
          <Route path="/disclaimer" element={<><Seo title="Disclaimer" description="Important disclaimers regarding the information, services, and technology content published on the Bytevora website." path="/disclaimer" /><LegalPage content={disclaimer} /></>} />
          <Route path="/terms-and-conditions" element={<><Seo title="Terms & Conditions" description="The terms governing use of the Bytevora website, enquiries, quotations, payments, and services." path="/terms-and-conditions" /><LegalPage content={termsAndConditions} /></>} />
          <Route path="/refund-cancellation-policy" element={<><Seo title="Refund & Cancellation Policy" description="How cancellations, refunds, project payments, third-party costs, and digital service charges are handled by Bytevora." path="/refund-cancellation-policy" /><LegalPage content={refundPolicy} /></>} />
          <Route path="/shipping-delivery-policy" element={<><Seo title="Shipping & Delivery Policy" description="How Bytevora delivers software, digital services, project files, credentials, cloud deployments, and other technology deliverables." path="/shipping-delivery-policy" /><LegalPage content={shippingPolicy} /></>} />
          <Route path="*" element={<ComingSoon />} />
        </Routes>
        <Footer />
        <CookieConsent />
        <div className="grain" aria-hidden="true" />
      </BrowserRouter>
    </div>
  );
}
